import { createContext, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { withTheme } from '@buoyhealth/common.buoy-theme';
import CloseIcon from '@mui/icons-material/Close';

import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import ProgressBar from '../ProgressBar';
import useLoading from '../../hooks/useLoading';
import useInitializing from '../../hooks/useInitializing';
import useUncaughtErrors from '../../hooks/useUncaughtErrors';
import ErrorContainer from '../ErrorContainer';
import LoadingContainer from '../LoadingContainer';
import { IPolarisLayoutProps } from './types';
import useInterviewType from '../../hooks/useInterviewType';
import { trackPolarisExit, trackPolarisUIImpression } from '../../analytics';
import useInterview from '../../hooks/useInterview';
import useChatList from '../../hooks/useChatList';

const StyledIconButton = styled(IconButton)``;
const StyledProgressBar = styled(ProgressBar)`
  margin-bottom: 20px;
  margin-top: 0;
  height: 6px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    width: 100%;
  }
`;

const Layout = ({
  children,
  handleExit = () => {},
  isEmbedded = false,
  showProgressBar = true,
  title,
  triggerRestart,
}: IPolarisLayoutProps) => {
  const errors = useUncaughtErrors();
  const isLoading = useLoading();
  const isInitializing = useInitializing();
  const interviewType = useInterviewType();
  const interview = useInterview();
  const chatList = useChatList();
  const showLoader = isLoading || isInitializing;
  useEffect(() => {
    trackPolarisUIImpression(interview.token || '', interviewType);
  }, []);
  const Content = useMemo(() => {
    if (errors.length) {
      return <ErrorContainer triggerRestart={triggerRestart} />;
    }
    if (showLoader) {
      return <LoadingContainer />;
    }
    return children;
  }, [children, errors.length, showLoader, triggerRestart]);
  const onExitClick = () => {
    trackPolarisExit(interview.token || '', interviewType, 'close icon click');
    handleExit();
  };

  return (
    <div className="BuoyInterviews__Layout BuoyInterviews__Layout__Polaris bp-flex bp-h-full bp-flex-col bp-overflow-hidden">
      {title &&
        (isEmbedded ? (
          <header className="bp-flex bp-min-h-12 bp-w-full bp-shrink-0 bp-items-center bp-justify-between bp-bg-primary-main bp-px-4 bp-py-2 bp-text-white">
            {title}
            <StyledIconButton onClick={onExitClick} size="small">
              <CloseIcon sx={{ color: '#FFF' }} fontSize="small" />
            </StyledIconButton>
          </header>
        ) : (
          <header className="bp-min-h-12 bp-w-full bp-shrink-0 bp-bg-primary-main bp-px-4 bp-py-2 bp-pr-14 bp-text-white">
            {title}
          </header>
        ))}

      <div className="bp-mt-6 bp-shrink-0 bp-px-4">{showProgressBar && <StyledProgressBar />}</div>

      <Grid
        className="bp-flex bp-h-full bp-flex-col bp-pt-0"
        container
        direction="column"
        flexWrap="nowrap"
        key={chatList.active}
        p="16px"
        spacing={2.5}
      >
        {Content}
      </Grid>
    </div>
  );
};

export default withTheme(Layout);
